import React, { useCallback, useEffect, useState, useMemo } from 'react';
import styled from 'styled-components';
import { SPACERS } from 'shared/design-system/settings/variables/Spacers';
import TrueImpactSimpleModal from 'shared/design-system/components/TrueImpactSimpleModal';
import TrueImpactTextfield from 'shared/design-system/components/TrueImpactTextfield';
import TrueImpactTextIndicator from 'shared/design-system/components/TrueImpactTextIndicator';
import TrueImpactAlertIcon from 'shared/design-system/components/TrueImpactAlertIcon';
import { faTriangleExclamation } from '@fortawesome/pro-light-svg-icons';
import {
  ReachForecastMatch,
  GuessMeasurementResult,
  IsHumanResult,
  ForecastResultOneResult,
} from './forecastValidationUtils';
import { COLORS } from 'shared/design-system/settings/variables/Colors';
import TrueImpactText from 'shared/design-system/base/TrueImpactText';
import { sectionKeyToString } from '../../../../ReportBuilder/Components/IndicatorDetails/utils';

const DEFAULT_MAX_NOTE_LENGTH = 500;

const StyledDiv = styled.div`
  padding: ${SPACERS.large} ${SPACERS.medium} ${SPACERS.medium};
  .error-message {
    margin-top: ${SPACERS.small};
    font-weight: 500;
  }
  .icon-row {
    display: flex;
    justify-content: center;
    margin-bottom: ${SPACERS.medium};
  }
  .message {
    margin: 0;
    line-height: 1.7;
    margin-bottom: ${SPACERS.medium};
  }
  .matches-container {
    margin-bottom: ${SPACERS.medium};
  }
  .indicator-match {
    padding: 0 ${SPACERS.medium} 0 ${SPACERS.medium};
    background: ${COLORS.greyLightest};
    border-radius: 4px;
  }
  .section-heading {
    font-weight: 600;
    font-size: 16px;
    color: ${COLORS.blackBase};
    margin-top: ${SPACERS.medium};
    margin-bottom: ${SPACERS.small};
    padding-bottom: ${SPACERS.tiny};
    border-bottom: 1px solid ${COLORS.greyLight};
  }
  .section-description {
    font-size: 14px;
    margin-bottom: ${SPACERS.small};
    line-height: 1.5;
  }
  .indicator-name {
    font-weight: 600;
    margin-bottom: ${SPACERS.tiny};
    color: ${COLORS.blackBase};
  }
  .match-details {
    margin: ${SPACERS.tiny} 0;
    padding-left: ${SPACERS.small};
  }
  .match-item {
    margin-bottom: ${SPACERS.tiny};
    font-size: 14px;
    color: ${COLORS.greyDark};
  }
  .note-wrapper {
    margin-top: ${SPACERS.small};
  }
  .note-label {
    font-size: 14px;
    font-weight: 500;
    margin-bottom: ${SPACERS.tiny};
    color: ${COLORS.blackBase};
  }
`;

export type IndicatorNoteField =
  | 'level1Forecast'
  | 'level1Guess'
  | 'level1IsHuman'
  | 'level1ForecastResultOne';

export interface IndicatorNote {
  indicatorId: number;
  note: string;
  field: IndicatorNoteField;
}

interface LevelOneFeedbackModalProps {
  open: boolean;
  onClose: () => void;
  onConfirm: (notes: IndicatorNote[]) => void;
  reachForecastMatch: ReachForecastMatch[];
  guessMeasurements?: GuessMeasurementResult[];
  isHumanIndicators?: IsHumanResult[];
  forecastResultOne?: ForecastResultOneResult[];
  sourceSectionLabel: string;
  targetSectionLabel: string;
  isInterimStage: boolean;
  isSaving?: boolean;
  errorMessage?: string;
}

const noteKey = (indicatorId: number, field: IndicatorNoteField): string =>
  `${indicatorId}-${field}`;

const buildNotesData = (
  isHumanIndicators: IsHumanResult[],
  reachForecastMatch: ReachForecastMatch[],
  guessMeasurements: GuessMeasurementResult[],
  forecastResultOne: ForecastResultOneResult[],
  notes: Record<string, string> = {},
): {
  initialNotes: Record<string, string>;
  indicatorNotes: IndicatorNote[];
} => {
  const initialNotes: Record<string, string> = {};
  const indicatorNotes: IndicatorNote[] = [];

  isHumanIndicators.forEach(result => {
    const key = noteKey(result.indicator.indicatorId, 'level1IsHuman');
    initialNotes[key] = result.level1IsHuman || '';
    indicatorNotes.push({
      indicatorId: result.indicator.indicatorId,
      field: 'level1IsHuman',
      note: notes[key] || '',
    });
  });

  reachForecastMatch.forEach(result => {
    const key = noteKey(result.indicator.indicatorId, 'level1Forecast');
    initialNotes[key] = result.level1Forecast || '';
    indicatorNotes.push({
      indicatorId: result.indicator.indicatorId,
      field: 'level1Forecast',
      note: notes[key] || '',
    });
  });

  guessMeasurements.forEach(result => {
    const key = noteKey(result.indicator.indicatorId, 'level1Guess');
    initialNotes[key] = result.level1Guess || '';
    indicatorNotes.push({
      indicatorId: result.indicator.indicatorId,
      field: 'level1Guess',
      note: notes[key] || '',
    });
  });

  forecastResultOne.forEach(result => {
    const key = noteKey(
      result.indicator.indicatorId,
      'level1ForecastResultOne',
    );
    initialNotes[key] = result.level1ForecastResultOne || '';
    indicatorNotes.push({
      indicatorId: result.indicator.indicatorId,
      field: 'level1ForecastResultOne',
      note: notes[key] || '',
    });
  });

  return { initialNotes, indicatorNotes };
};

const ForecastMatchesDescription: React.FC<{
  sourceSectionLabel: string;
  targetSectionLabel: string;
  matchCount: number;
}> = ({ sourceSectionLabel, targetSectionLabel, matchCount }) => (
  <>
    <div className="section-heading">Forecast Matches</div>
    <div className="section-description">
      The following <strong>{sourceSectionLabel}</strong>{' '}
      {matchCount === 1 ? 'indicator' : 'indicators'} match{' '}
      <strong>{targetSectionLabel}</strong> forecasts. Program Development is
      meant to capture how your program improved internally this year and not
      who you reached. Please provide an explanation for each indicator below.
    </div>
  </>
);

const GuessMeasurementsDescription: React.FC = () => (
  <>
    <div className="section-heading">Guess Measurements</div>
    <div className="section-description">
      Some of your report&rsquo;s indicators use <strong>Guess-level</strong>{' '}
      measurements. Donors often find Evidence-based Estimates or Direct
      Measurements more compelling, so consider improving these where feasible.
      For practical ideas, see{' '}
      <a
        href="https://support.trueimpact.com/improving-data-quality-1"
        target="_blank"
        rel="noopener noreferrer"
      >
        Improving Data Quality
      </a>
      {' & '}
      <a
        href="https://support.trueimpact.com/true-impacts-top-5-data-collection-best-practices-1"
        target="_blank"
        rel="noopener noreferrer"
      >
        Top 5 Data Collection Best Practices
      </a>
      .
    </div>
  </>
);

const IsHumanDescription: React.FC = () => (
  <>
    <div className="section-heading">Reach is Human</div>
    <div className="section-description">
      You have multiple reach indicators marked as measuring humans. Please
      provide an explanation for the following indicator to clarify how it
      differs from your other human reach measurements.
    </div>
  </>
);

const ForecastResultOneDescription: React.FC = () => (
  <>
    <div className="section-heading">Forecast Result of One</div>
    <div className="section-description">
      The following indicators have a forecast or final result of 1. Are you
      sure these are accurate? Please provide your reasoning for each indicator
      below.
    </div>
  </>
);

const LevelOneFeedbackModal: React.FC<LevelOneFeedbackModalProps> = ({
  open,
  onClose,
  onConfirm,
  reachForecastMatch,
  guessMeasurements = [],
  isHumanIndicators = [],
  forecastResultOne = [],
  sourceSectionLabel,
  targetSectionLabel,
  isInterimStage,
  isSaving = false,
  errorMessage,
}) => {
  const [notes, setNotes] = useState<Record<string, string>>({});

  useEffect(() => {
    if (open) {
      const { initialNotes } = buildNotesData(
        isHumanIndicators,
        reachForecastMatch,
        guessMeasurements,
        forecastResultOne,
      );
      setNotes(initialNotes);
    }
  }, [
    open,
    reachForecastMatch,
    guessMeasurements,
    isHumanIndicators,
    forecastResultOne,
  ]);

  const handleNoteChange = useCallback(
    (indicatorId: number, field: IndicatorNoteField, value: string) => {
      setNotes(prev => ({ ...prev, [noteKey(indicatorId, field)]: value }));
    },
    [],
  );

  const handleConfirm = useCallback(() => {
    const { indicatorNotes } = buildNotesData(
      isHumanIndicators,
      reachForecastMatch,
      guessMeasurements,
      forecastResultOne,
      notes,
    );
    onConfirm(indicatorNotes);
  }, [
    notes,
    reachForecastMatch,
    guessMeasurements,
    isHumanIndicators,
    forecastResultOne,
    onConfirm,
  ]);

  const allForecastNotesValid = useMemo(
    () =>
      reachForecastMatch.every(result => {
        const note =
          notes[noteKey(result.indicator.indicatorId, 'level1Forecast')] || '';
        return note.trim() !== '' && note.length <= DEFAULT_MAX_NOTE_LENGTH;
      }),
    [notes, reachForecastMatch],
  );

  const allGuessNotesValid = useMemo(
    () =>
      guessMeasurements.every(result => {
        const note =
          notes[noteKey(result.indicator.indicatorId, 'level1Guess')] || '';
        return note.trim() !== '' && note.length <= DEFAULT_MAX_NOTE_LENGTH;
      }),
    [notes, guessMeasurements],
  );

  const allIsHumanNotesValid = useMemo(
    () =>
      isHumanIndicators.every(result => {
        const note =
          notes[noteKey(result.indicator.indicatorId, 'level1IsHuman')] || '';
        return note.trim() !== '' && note.length <= DEFAULT_MAX_NOTE_LENGTH;
      }),
    [notes, isHumanIndicators],
  );

  const allForecastResultOneNotesValid = useMemo(
    () =>
      forecastResultOne.every(result => {
        const note =
          notes[
            noteKey(result.indicator.indicatorId, 'level1ForecastResultOne')
          ] || '';
        return note.trim() !== '' && note.length <= DEFAULT_MAX_NOTE_LENGTH;
      }),
    [notes, forecastResultOne],
  );

  const allNotesValid =
    allForecastNotesValid &&
    allGuessNotesValid &&
    allIsHumanNotesValid &&
    allForecastResultOneNotesValid;

  const hasMatches = reachForecastMatch.length > 0;
  const hasGuesses = guessMeasurements.length > 0;
  const hasIsHuman = isHumanIndicators.length > 0;
  const hasForecastResultOne = forecastResultOne.length > 0;

  const getMatchFieldLabel = useCallback(
    (result: ReachForecastMatch): string => {
      const fields: string[] = [];
      if (result.forecastMatches.length > 0) fields.push('Forecast');
      if (result.finalForecastMatches.length > 0) {
        fields.push(isInterimStage ? 'Updated Forecast' : 'Final Results');
      }
      return fields.join(' and ');
    },
    [isInterimStage],
  );

  return (
    <TrueImpactSimpleModal
      open={open}
      onClose={onClose}
      contentStyle={{
        maxWidth: 700,
        maxHeight: '90vh',
        borderRadius: 4,
        overflowY: 'auto',
        display: 'flex',
        flexDirection: 'column',
      }}
      next={{
        text: 'Save & Continue',
        onClick: handleConfirm,
        disabled: !allNotesValid || isSaving,
        isLoading: isSaving,
      }}
      previous={{
        text: 'Cancel',
        onClick: onClose,
        disabled: isSaving,
      }}
    >
      <StyledDiv>
        <div className="icon-row">
          <TrueImpactAlertIcon
            icon={faTriangleExclamation}
            color="warningDark"
            shadowOpacity={0.15}
          />
        </div>
        {hasIsHuman && (
          <>
            <IsHumanDescription />
            <div className="matches-container">
              {isHumanIndicators.map((result, idx) => {
                const key = noteKey(
                  result.indicator.indicatorId,
                  'level1IsHuman',
                );
                const note = notes[key] || '';
                return (
                  <div
                    key={result.indicator.indicatorId}
                    className="indicator-match"
                  >
                    <div className="indicator-name">
                      {idx + 1}. {result.indicator.indicatorName}
                    </div>
                    <div className="note-wrapper">
                      <div className="note-label">Explanation *</div>
                      <TrueImpactTextfield
                        isTextarea
                        placeholder="Please explain how this indicator differs from your other human reach measurements..."
                        value={note}
                        onChange={value =>
                          handleNoteChange(
                            result.indicator.indicatorId,
                            'level1IsHuman',
                            value,
                          )
                        }
                        maxLength={DEFAULT_MAX_NOTE_LENGTH}
                      />
                      <TrueImpactTextIndicator
                        minLength={0}
                        text={note}
                        maxLength={DEFAULT_MAX_NOTE_LENGTH}
                      />
                    </div>
                  </div>
                );
              })}
            </div>
          </>
        )}
        {hasMatches && (
          <>
            <ForecastMatchesDescription
              sourceSectionLabel={sourceSectionLabel}
              targetSectionLabel={targetSectionLabel}
              matchCount={reachForecastMatch.length}
            />
            <div className="matches-container">
              {reachForecastMatch.map((result, idx) => {
                const key = noteKey(
                  result.indicator.indicatorId,
                  'level1Forecast',
                );
                const note = notes[key] || '';
                return (
                  <div
                    key={result.indicator.indicatorId}
                    className="indicator-match"
                  >
                    <div className="indicator-name">
                      {idx + 1}. {result.indicator.indicatorName}
                    </div>
                    <div className="match-details">
                      <div className="match-item">
                        <strong>Field:</strong> {getMatchFieldLabel(result)}
                      </div>
                      <div className="match-item">
                        <strong>Matches:</strong>
                      </div>
                      {result.forecastMatches.map(t => (
                        <div
                          key={`f-${result.indicator.indicatorId}-${t.indicatorId}`}
                          className="match-item"
                          style={{ paddingLeft: SPACERS.small }}
                        >
                          • {t.indicatorName} (Forecast)
                        </div>
                      ))}
                      {result.finalForecastMatches.map(t => (
                        <div
                          key={`ff-${result.indicator.indicatorId}-${t.indicatorId}`}
                          className="match-item"
                          style={{ paddingLeft: SPACERS.small }}
                        >
                          • {t.indicatorName} (
                          {isInterimStage
                            ? 'Updated Forecast'
                            : 'Final Results'}
                          )
                        </div>
                      ))}
                    </div>
                    <div className="note-wrapper">
                      <div className="note-label">Explanation *</div>
                      <TrueImpactTextfield
                        isTextarea
                        placeholder="Please explain why these values match..."
                        value={note}
                        onChange={value =>
                          handleNoteChange(
                            result.indicator.indicatorId,
                            'level1Forecast',
                            value,
                          )
                        }
                        maxLength={DEFAULT_MAX_NOTE_LENGTH}
                      />
                      <TrueImpactTextIndicator
                        minLength={0}
                        text={note}
                        maxLength={DEFAULT_MAX_NOTE_LENGTH}
                      />
                    </div>
                  </div>
                );
              })}
            </div>
          </>
        )}
        {hasGuesses && (
          <>
            <GuessMeasurementsDescription />
            <div className="matches-container">
              {guessMeasurements.map((result, idx) => {
                const key = noteKey(
                  result.indicator.indicatorId,
                  'level1Guess',
                );
                const note = notes[key] || '';
                return (
                  <div
                    key={result.indicator.indicatorId}
                    className="indicator-match"
                  >
                    <div className="indicator-name">
                      {idx + 1}. {result.indicator.indicatorName}
                    </div>
                    <div className="match-details">
                      <div className="match-item">
                        <strong>Measurement:</strong> {result.measurement}
                      </div>
                    </div>
                    <div className="note-wrapper">
                      <div className="note-label">Reason for guess *</div>
                      <TrueImpactTextfield
                        isTextarea
                        placeholder="Please explain the basis for this guess..."
                        value={note}
                        onChange={value =>
                          handleNoteChange(
                            result.indicator.indicatorId,
                            'level1Guess',
                            value,
                          )
                        }
                        maxLength={DEFAULT_MAX_NOTE_LENGTH}
                      />
                      <TrueImpactTextIndicator
                        minLength={0}
                        text={note}
                        maxLength={DEFAULT_MAX_NOTE_LENGTH}
                      />
                    </div>
                  </div>
                );
              })}
            </div>
          </>
        )}
        {hasForecastResultOne && (
          <>
            <ForecastResultOneDescription />
            <div className="matches-container">
              {forecastResultOne.map((result, idx) => {
                const key = noteKey(
                  result.indicator.indicatorId,
                  'level1ForecastResultOne',
                );
                const note = notes[key] || '';
                return (
                  <div
                    key={result.indicator.indicatorId}
                    className="indicator-match"
                  >
                    <div className="indicator-name">
                      {idx + 1}. {result.indicator.indicatorName}
                    </div>
                    <div className="match-details">
                      <div className="match-item">
                        <strong>Section:</strong>{' '}
                        {sectionKeyToString(result.sectionKey)}
                      </div>
                      {result.forecastValue === '1' && (
                        <div className="match-item">
                          <strong>Forecast:</strong> 1
                        </div>
                      )}
                      {result.finalForecastValue === '1' && (
                        <div className="match-item">
                          <strong>
                            {isInterimStage
                              ? 'Updated Forecast'
                              : 'Final Results'}
                            :
                          </strong>{' '}
                          1
                        </div>
                      )}
                    </div>
                    <div className="note-wrapper">
                      <div className="note-label">Reasoning *</div>
                      <TrueImpactTextfield
                        isTextarea
                        placeholder="Please provide your reasoning..."
                        value={note}
                        onChange={value =>
                          handleNoteChange(
                            result.indicator.indicatorId,
                            'level1ForecastResultOne',
                            value,
                          )
                        }
                        maxLength={DEFAULT_MAX_NOTE_LENGTH}
                      />
                      <TrueImpactTextIndicator
                        minLength={0}
                        text={note}
                        maxLength={DEFAULT_MAX_NOTE_LENGTH}
                      />
                    </div>
                  </div>
                );
              })}
            </div>
          </>
        )}
        {errorMessage && (
          <TrueImpactText color="critical" className="error-message">
            {errorMessage}
          </TrueImpactText>
        )}
      </StyledDiv>
    </TrueImpactSimpleModal>
  );
};

export default LevelOneFeedbackModal;
